import { sorter, userFullNameSortLabel } from 'util/Table';
import { SortConfig } from 'store/types/Table';
import CompanyUser from 'store/types/CompanyUser';
import { getUserFullName } from 'util/Format';

export enum CompanyUsersTableActionType {
  SetInitialList = 'SetInitialList',
  UpdateSort = 'UpdateSort',
  UpdateFilter = 'UpdateFilter',
  OpenRemoveModal = 'OpenRemoveModal',
  CloseRemoveModal = 'CloseRemoveModal',
  OpenViewUserModal = 'OpenViewUserModal',
  CloseViewUserModal = 'CloseViewUserModal',
}

export type TableCompanyUsers = CompanyUser & { [userFullNameSortLabel]?: string };

export interface CompanyUsersTableState {
  initialList: TableCompanyUsers[];
  list: TableCompanyUsers[];
  sort: SortConfig<TableCompanyUsers>;
  clickedItem?: TableCompanyUsers;
  selectedUser?: TableCompanyUsers;
}

export interface CompanyUsersTableAction {
  type: CompanyUsersTableActionType;
  payload: Partial<Omit<CompanyUsersTableState, 'initialList'>> & {
    initialList?: CompanyUser[];
    selectedItem?: TableCompanyUsers;
  };
}

const getFunctionSortData = (user: TableCompanyUsers): { priority: number; value: string } => {
  const functions = user.functions ?? [];
  const hasAdminFunction = functions.some((customerFunction) => customerFunction.isAdminFunction);

  const value = [...functions]
    .sort((first, second) => {
      if (first.isAdminFunction !== second.isAdminFunction) {
        return first.isAdminFunction ? -1 : 1;
      }

      return first.functionName?.localeCompare(second.functionName);
    })
    .map((customerFunction) => customerFunction.functionName?.trim())
    .filter(Boolean)
    .join('|');

  return {
    priority: hasAdminFunction ? 0 : functions.length ? 1 : 2,
    value,
  };
};

const getSortedList = (list: TableCompanyUsers[] = [], sort: SortConfig<TableCompanyUsers>): TableCompanyUsers[] => {
  if (!list.length) {
    return [];
  }

  if (sort.column !== 'functions') {
    return [...list].sort(sorter<TableCompanyUsers>(sort));
  }

  const direction = sort.direction === 'desc' ? -1 : 1;

  return [...list].sort((first, second) => {
    const firstFunctions = getFunctionSortData(first);
    const secondFunctions = getFunctionSortData(second);

    const priorityComparison = firstFunctions.priority - secondFunctions.priority;

    if (priorityComparison !== 0) {
      return priorityComparison;
    }

    const functionComparison = firstFunctions.value.localeCompare(secondFunctions.value);

    if (functionComparison !== 0) {
      return functionComparison * direction;
    }

    const firstName = first[userFullNameSortLabel] ?? '';
    const secondName = second[userFullNameSortLabel] ?? '';

    return firstName.localeCompare(secondName) * direction;
  });
};

const toTableUser = (user: TableCompanyUsers): TableCompanyUsers => ({
  ...user,
  [userFullNameSortLabel]: getUserFullName(user, true),
});

export const initialState: CompanyUsersTableState = {
  initialList: [],
  list: [],
  sort: {
    direction: 'asc',
    column: 'functions',
  },
  clickedItem: undefined,
};

const reducer = (state: CompanyUsersTableState, { type, payload }: CompanyUsersTableAction): CompanyUsersTableState => {
  if (type === CompanyUsersTableActionType.SetInitialList) {
    const { initialList = [] } = payload;

    const tableList = initialList.map(toTableUser);

    return { ...state, initialList, list: getSortedList(tableList, state.sort) };
  }

  if (type === CompanyUsersTableActionType.UpdateSort) {
    const { sort = {} } = payload;
    const newList = getSortedList(state.initialList, sort);

    return { ...state, sort, list: newList };
  }

  if (type === CompanyUsersTableActionType.OpenRemoveModal) {
    const { clickedItem } = payload;

    return { ...state, clickedItem };
  }

  if (type === CompanyUsersTableActionType.CloseRemoveModal) {
    return { ...state, clickedItem: undefined };
  }

  if (type === CompanyUsersTableActionType.OpenViewUserModal) {
    const { selectedUser } = payload;

    return { ...state, selectedUser };
  }

  if (type === CompanyUsersTableActionType.CloseViewUserModal) {
    return { ...state, selectedUser: undefined };
  }

  return state;
};

export default reducer;
